<script setup lang="ts">
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'

const { t, locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const getLocalizedText = (
  value: FeedActivity['title'] | FeedActivity['description'] | string | null | undefined
): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[locale.value as keyof typeof value] || value.en || ''
  }
  return ''
}

const description = computed(() => {
  const descText = getLocalizedText(props.activity.description)
  if (descText) return descText

  const answerText = props.activity.data?.answer_text
  const translationKey =
    answerText === null || answerText === undefined
      ? 'pages.customer.customerDetailPage.blocks.activity.survey.text.noAnswer'
      : 'pages.customer.customerDetailPage.blocks.activity.survey.text.answer'

  return t(translationKey, { choice: answerText || '' })
})

const iconColor = computed(() => {
  return 'bg-indigo-400'
})

const detailTitle = computed(() => {
  return t('pages.customer.customerDetailPage.blocks.activity.survey.detail.title')
})

const detailText = computed(() => {
  return props.activity.data?.question_text || ''
})
</script>

<template>
  <ActivityCardBase
    :icon="activity.icon || 'HeroChartPieIconSolid'"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template #description>
      <p class="flex text-sm text-muted-foreground">
        <Popover v-if="detailText">
          <PopoverTrigger class="border-b border-dashed text-left focus:outline-hidden">
            {{ description }}
          </PopoverTrigger>
          <PopoverContent class="w-80 overflow-hidden border p-0 shadow-lg">
            <div class="bg-muted p-4">
              <span class="flex items-center">
                <span class="text-sm font-medium text-foreground">{{ detailTitle }}</span>
              </span>
              <span v-if="detailText" class="mt-1 block text-sm text-muted-foreground">
                {{ detailText }}
              </span>
            </div>
          </PopoverContent>
        </Popover>
        <span v-else>{{ description }}</span>
      </p>
    </template>
  </ActivityCardBase>
</template>
